import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import GeneralEmbeddedApp from 'lumen/components/general/embedded-app';
import type DestinationReportingDashboardController from 'lumen/controllers/destination/reporting/dashboard';

interface DestinationReportingDashboardSignature {
  Args: {
    controller: DestinationReportingDashboardController;
    model: unknown;
  };
}

<template>
  <div class="flex w-full flex-col">
    <div
      class="custom-reporting__dashboard-header flex items-center justify-between pt-200 pr-800 pb-200 pl-800"
    >
      <div class="flex items-center">
        <span
          class="dashboard-title"
          data-test-id="dashboard-title"
        >{{@controller.model.title}}</span>
      </div>
      {{#if @controller.customReporting.dashboardLoading}}
        <div
          class="loading-pulse loading-pulse--col-3"
          data-test-id="dashboard-loading-pulse"
        ></div>
      {{else}}
        <div
          class="flex items-center gap-300"
          data-test-id="dashboard-header-options"
        >
          {{#if
            (and
              (not @controller.isEmpty)
              (not @controller.isRefreshRunning)
              @controller.lastRunAtFormatted
            )
          }}
            <span class="text-200--medium" data-test-id="dashboard-last-run-at">
              {{t
                "custom_reporting.data_updated_at"
                value=@controller.lastRunAtFormatted
              }}
            </span>
          {{/if}}

          <PrismButton
            @iconBefore="sync"
            @isDisabled={{or @controller.isEmpty @controller.isRefreshRunning}}
            @isLoading={{@controller.isRefreshRunning}}
            @label={{t "custom_reporting.refresh_data"}}
            @onClick={{@controller.triggerRefresh}}
            data-test-id="dashboard-refresh-button"
          />
          <PrismComboSelectInput
            @disabled={{@controller.isEmpty}}
            @onChange={{@controller.handleExport}}
            @options={{@controller.exportOptions}}
            @unsafe_matchTriggerWidth={{true}}
            @value={{@controller.exportValue}}
            data-test-id="dashboard-export-dropdown"
          />
          {{#if @controller.access.hasCustomReportingEdit}}
            {{#if @controller.canEdit}}
              <PrismButton
                @buttonType="secondary"
                @iconBefore="edit-pencil"
                @label={{t "general.edit"}}
                @onClick={{@controller.editDashboard}}
                data-test-id="dashboard-edit-button"
              />
            {{else}}
              <PrismButton
                @buttonType="secondary"
                @iconBefore="copy"
                @label={{t "general.copy"}}
                @onClick={{@controller.copyDashboard}}
                data-test-id="dashboard-copy-button"
              />
            {{/if}}
          {{/if}}
          {{! @TODO vfuto - replace this to prism/button once it's in the type registry }}
          {{#if
            (or
              (not @controller.isEmpty)
              (and
                @controller.access.hasCustomReportingEdit
                @controller.model.folder.canEdit
              )
            )
          }}
            <PrismComboSelectInput
              @horizontalPosition="right"
              @onChange={{@controller.onEditOptionSelect}}
              @options={{@controller.editOptions}}
              @unsafe_dropdownClass={{if
                @controller.canEdit
                "custom-reporting--actions-dropdown"
              }}
              @unsafe_triggerComponent={{component
                GeneralDropdownsTriggersTriggerDropdownIconButton
                buttonType="ghost"
                icon="kebab"
                small=false
              }}
              data-test-id="dashboard-actions-dropdown"
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
    <div class="embed-container">
      <GeneralEmbeddedApp
        {{! @glint-expect-error: Incorrect type }}
        @baseURL={{@controller.ajax.host}}
        @id="looker-embed"
        {{! @glint-expect-error: Incorrect type }}
        @onMessage={{@controller.onMessage}}
        @query={{@controller.iframeQPs}}
        @url={{concat
          "/api/v3/custom-reporting/embed/dashboards/"
          @controller.model.id
        }}
      />
    </div>
  </div>
</template> satisfies TOC<DestinationReportingDashboardSignature>;
